import { appLoaderHeight, headerBarHeight } from '@components/AppLayout/consts'
import type { TypeBackground } from '@mui/material'
import { Chip, LinearProgress, styled } from '@mui/material'

export const Root = styled('div')(({ theme }) => ({
  isolation: 'isolate',
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
  width: `100%`,
}))

export const Content = styled('div')<{ background: keyof TypeBackground }>(
  ({ theme, background }) => ({
    [theme.breakpoints.up('md')]: {
      width: 0,
    },
    backgroundColor: theme.palette.background[background],
    flex: 1,
    // Anchor for the absolutely-positioned AppLoader overlay below.
    position: 'relative',
  }),
)

// A thin progress bar pinned over the top edge of the content. It used to sit
// in flow (a 4px strip + an equal-height placeholder reserving the row), which
// cost vertical space on every screen. As an overlay it costs zero layout
// height while remaining the single loading signal for both the `makeQuery`
// hooks and Suspense fallbacks (`useAppLoader`).
export const AppLoader = styled(LinearProgress)({
  height: appLoaderHeight,
  left: 0,
  position: 'absolute',
  right: 0,
  top: 0,
  zIndex: 1201,
})

export const ViewContainer = styled('div')(() => ({
  minHeight: `calc(100vh - ${headerBarHeight}px)`,
}))

export const HeaderWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'hidden',
})<{ hidden?: boolean }>(({ hidden, theme }) => ({
  // Full-bleed unified header bar: paper surface + a bottom divider, shown only
  // when the header actually carries content. Screens that manage their own
  // chrome (e.g. the messaging workspace, which renders no PageHeader) leave the
  // header empty and get no bar.
  '&:has(.app-header-bar:not(:empty))': {
    background: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  // Full-screen views (e.g. a mobile messaging thread) hide the bar entirely so
  // they can supply their own chrome — see useHideAppHeader.
  ...(hidden ? { display: 'none' } : {}),
}))

export const Header = styled('div')(({ theme }) => ({
  // Leading slot swaps: a back link replaces the hamburger (never both). The
  // hamburger only renders below `md` anyway; this hides it on detail screens.
  '&:has(.header-backlink) .header-drawer-menu': {
    display: 'none',
  },
  // A real bar height only when populated — see HeaderWrapper.
  '&:not(:empty)': {
    height: headerBarHeight,
  },
  alignItems: 'center',
  display: 'flex',
  gap: theme.spacing(1.5),
}))

export const MainView = styled('main')({
  marginLeft: 0,
})

export const AppAction = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    left: 0,
    width: '100%',
  },
  bottom: 0,
  gridColumn: 2,
  position: 'sticky',
  zIndex: 10,
}))

export const SideBar = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    padding: 0,
  },
  background: theme.palette.background.paper,
  padding: `0 ${theme.spacing(4)}px`,
}))

export const UserChip = styled(Chip)(({ theme }) => ({
  '& .MuiAvatar-root': {
    height: 32,
    width: 32,
  },
  backgroundColor: theme.palette.background.paper,
  borderRadius: 24,
  color: theme.palette.text.primary,
  height: 40,
  padding: theme.spacing(0.5),
}))
